import { ConfigStateService, LocalizationService, PermissionService } from "@abp/ng.core";
import { ToasterService } from "@abp/ng.theme.shared";
import { Injectable, signal } from "@angular/core";
import { CertificationService } from "src/app/proxy/certifications";
import { ProjectService } from "src/app/proxy/projects";
import { RoadService } from "src/app/proxy/roads";
import { firstValueFrom } from "rxjs";
import { User } from "../entities/user";
import {
    SynchronizationStep,
    SynchronizationStepDefinition
} from "./dtos/synchronization-step.dto";
import { CertificationInputRepository } from "../local-db/repositories/certification-input-repository";
import { CertificationItemRepository } from "../local-db/repositories/certification-item-repository";
import { CertificationRepository } from "../local-db/repositories/certification-repository";
import { ProjectRepository } from "../local-db/repositories/project-repository";
import { RoadRepository } from "../local-db/repositories/road-repository";
import { SyncDataRepository } from "../local-db/repositories/sync-data-repository";
import { ProgressRepository } from "../local-db/repositories/progress-repository";
import { ProgressService } from "src/app/proxy/progresses";
import { ProgressItemRepository } from "../local-db/repositories/progress-item-repository";
import { UserRepository } from "../local-db/repositories/user-repository";
import { UserService } from "src/app/proxy/users";
import { ConnectionService } from "./connection-service";
import { CreateActivityReportInput } from "src/app/proxy/progresses/dtos";
import { Progress } from "../entities/progress";
import { ActivityReportService } from "src/app/proxy/activity-report";
import { Certification } from "../entities/certification";
import { SyncLogRepository } from "../local-db/repositories/sync-log-repository";
import { SyncLog } from "../entities/sync-log";
import { ActionEnum } from "../enums/actionEnum";
import { ActivityReportEnum } from "../enums/activityReportEnum";
import { SynchronizationStepName, SynchronizationStepStatus } from "./enums/synchronization";

@Injectable({ providedIn: 'root' })
export class SynchronizationManagerService {
    readonly syncSteps = signal<SynchronizationStep[]>([]);
    readonly isSyncInProgress = signal(false);

    private readonly stepDefinitions: ReadonlyArray<SynchronizationStepDefinition> = [
        { key: SynchronizationStepName.PendingCertifications, localizationKey: '::PendingCertifications' },
        { key: SynchronizationStepName.PendingProgress, localizationKey: '::PendingProgress' },
        { key: SynchronizationStepName.Projects, localizationKey: '::Projects' },
        { key: SynchronizationStepName.Roads, localizationKey: '::Roads' },
        { key: SynchronizationStepName.Certifications, localizationKey: '::Certifications' },
        { key: SynchronizationStepName.CertificationInputs, localizationKey: '::CertificationInputs' },
        { key: SynchronizationStepName.CertificationItems, localizationKey: '::CertificationItems' },
        { key: SynchronizationStepName.Progress, localizationKey: '::Progress' },
        { key: SynchronizationStepName.Progressitems, localizationKey: '::ProgressItems' },
    ];

    constructor(
        private readonly configStateService: ConfigStateService ,
        private readonly permissionService: PermissionService,
        private readonly certificationService: CertificationService,
        private readonly certificationRepository: CertificationRepository,
        private readonly certificationInputRepository: CertificationInputRepository,
        private readonly certificationItemRepository: CertificationItemRepository,
        private readonly projectService: ProjectService,
        private readonly projectRepository: ProjectRepository,
        private readonly roadService: RoadService,
        private readonly roadRepository: RoadRepository,
        private readonly progressService: ProgressService,
        private readonly progressRepository: ProgressRepository,
        private readonly progressItemRepository: ProgressItemRepository,
        private readonly userRepository: UserRepository,
        private readonly userService: UserService,
        private readonly syncDataRepository: SyncDataRepository,
        private readonly toaster: ToasterService,
        private readonly localization: LocalizationService,
        private readonly connectionService: ConnectionService,
        private readonly activityReportService: ActivityReportService,
        private readonly syncLogRepository: SyncLogRepository
    ) {}

    async syncAll(user?: User): Promise<void> {
        if (!this.connectionService.online()) {
            return;
        }

        if (this.isSyncInProgress()) {
            return;
        }

        const resolvedUser = await this.resolveCurrentUser(user);
        if (!resolvedUser) {
            return;
        }

        this.syncSteps.set(this.stepDefinitions.map(step => ({
            key: step.key,
            label: this.getLocalizedStepLabel(step),
            status: SynchronizationStepStatus.Pending,
        })));

        this.isSyncInProgress.set(true);

        const canGetProjectsBySupervisor = this.permissionService.getGrantedPolicy(
            'RoadsProjectManagement.Projects.BySupervisor'
        );

        try {
            const failedSteps: SynchronizationStepName[] = [];
            let bioforestUrl: string | undefined;

            await this.runSyncStep(SynchronizationStepName.PendingCertifications, failedSteps, async () => {
                await this.syncPendingCertifications();
            });

            await this.runSyncStep(SynchronizationStepName.PendingProgress, failedSteps, async () => {
                await this.syncPendingProgress();
            });

            await this.runSyncStep(SynchronizationStepName.Projects, failedSteps, async () => {
                let syncResult = [];
                if (canGetProjectsBySupervisor) {
                    syncResult = await firstValueFrom(
                        this.projectService.getBySupervisorBySupervisorId(user.id, { skipHandleError: true })
                    );
                } else {
                    syncResult = await firstValueFrom(
                        this.projectService.getByVendorByVendorId(user.vendorId, { skipHandleError: true })
                    );
                }

                await this.projectRepository.clear();
                await this.projectRepository.upsertBulk(syncResult ?? []);
            });

            const supervisorIds = await this.projectRepository.getAllSupervisorIds();

            await this.runSyncStep(SynchronizationStepName.Roads, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.roadService.getByInput({ supervisorIds: supervisorIds }, { skipHandleError: true })
                );

                await this.roadRepository.clear();
                await this.roadRepository.upsertBulk(syncResult ?? []);
            });

            await this.runSyncStep(SynchronizationStepName.Certifications, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.certificationService.getByInput({ supervisorIds }, { skipHandleError: true })
                );

                await this.certificationRepository.clear();
                await this.certificationRepository.upsertBulk(syncResult ?? []);
            });

            await this.runSyncStep(SynchronizationStepName.CertificationInputs, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.certificationService.getInputsByInput({ supervisorIds }, { skipHandleError: true })
                );

                await this.certificationInputRepository.clear();
                await this.certificationInputRepository.upsertBulk(syncResult ?? []);
            });

            await this.runSyncStep(SynchronizationStepName.CertificationItems, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.certificationService.getItemsByInput({ supervisorIds }, { skipHandleError: true })
                );

                await this.certificationItemRepository.clear();
                await this.certificationItemRepository.upsertBulk(syncResult ?? []);
            });

            await this.runSyncStep(SynchronizationStepName.Progress, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.progressService.getByInput({ supervisorIds }, { skipHandleError: true })
                );

                await this.progressRepository.clear();
                await this.progressRepository.upsertBulk(syncResult ?? []);
            });

            await this.runSyncStep(SynchronizationStepName.Progressitems, failedSteps, async () => {
                const syncResult = await firstValueFrom(
                    this.progressService.getItemsByInput({ supervisorIds }, { skipHandleError: true })
                );

                await this.progressItemRepository.clear();
                await this.progressItemRepository.upsertBulk(syncResult ?? []);
            });

            const syncDataToSave: { lastSyncAt: string; bioforestUrl?: string } = {
                lastSyncAt: new Date().toISOString(),
            };

            if (bioforestUrl !== undefined) {
                syncDataToSave.bioforestUrl = bioforestUrl;
            }

            await this.syncDataRepository.save(syncDataToSave);

        } finally {
            this.isSyncInProgress.set(false);
        }
    }

    private getLocalizedStepLabel(step: SynchronizationStepDefinition): string {
        return this.localization.instant(step.localizationKey);
    }

    private async runSyncStep(
        stepName: SynchronizationStepName,
        failedSteps: SynchronizationStepName[],
        action: () => Promise<void>
    ): Promise<void> {
        this.updateStep(stepName, {
        status: SynchronizationStepStatus.Running,
        });

        try {
            await action();
            this.updateStep(stepName, {
                status: SynchronizationStepStatus.Success,
            });
        } catch {
            failedSteps.push(stepName);
            this.updateStep(stepName, {
                status: SynchronizationStepStatus.Error,
            });
        }
    }

    private updateStep(
        stepName: SynchronizationStepName,
        partialStep: Partial<SynchronizationStep>
    ): void {
        this.syncSteps.update(steps =>
        steps.map(step =>
            step.key === stepName
            ? { ...step, ...partialStep }
            : step
        )
        );
    }

    private async syncPendingProgress(): Promise<void> {
        const pendingProgress = await this.progressRepository.getPendingSendProgresses();

        for (const pending of pendingProgress) {
            if (pending.isRejected) {
                await this.rejectPendingProgress(pending);
            } else {
                await this.postPendingProgress(pending);
            }
        }
    }

    private async syncPendingCertifications(): Promise<void> {
        const pendingCertifications = await this.certificationRepository.getPendingSendCertifications();

        for (const pending of pendingCertifications) {
            if (pending.isRejected) {
                await this.rejectPendingCertifications(pending);
            } else {
                await this.postPendingCertifications(pending);
            }
        }
    }

    private async postPendingProgress(progress: Progress) {
      try {
        const request: CreateActivityReportInput = {
          originId: progress.originId,
          projectId: progress.projectId,
          supervisorId: progress.userId,
          documentDate: progress.date,
          isCertification: false,
          activities: [],
        };
  
        const progressItems = await this.progressItemRepository.getAllByActivityReportId(progress.originId);
        request.activities = progressItems
        .filter(item => item.quantity && item.quantity > 0)
        .map(item => ({
          roadId: progress.roadId,
          activityId: item.activityId,
          quantity: item.quantity,
          consumptions: [],
        }));
  
        await firstValueFrom(this.activityReportService.createByInput({
          ...request,
          originApplication: 'RoadsProjectManagement',
        } as any));
  
        await this.progressRepository.setPendingSendByOriginId(progress.originId, false);
      } catch (error) {
        this.toaster.warn(
            `${this.localization.instant('::Synchronization:PendingProgressError')}`
        );
        const log: SyncLog = {
            originId: progress.originId,
            errorMessage: error.error.error.message,
            action: ActionEnum.Create,
            activityReport: ActivityReportEnum.Progress,
            createdAt: new Date().toISOString(),
        };
        this.syncLogRepository.create(log);
      }
    }

    private async rejectPendingProgress(progress: Progress) {
      try {
        await firstValueFrom(this.activityReportService.rejectBySourceId(progress.originId));
        await this.progressRepository.setPendingSendByOriginId(progress.originId, false);
      } catch (error) {
        this.toaster.warn(
            `${this.localization.instant('::Synchronization:PendingProgressError')}`
        );
        const log: SyncLog = {
            originId: progress.originId,
            errorMessage: error.error.error.message,
            action: ActionEnum.Reject,
            activityReport: ActivityReportEnum.Progress,
            createdAt: new Date().toISOString(),
        };
        this.syncLogRepository.create(log);
      }
    }

    private async postPendingCertifications(certification: Certification) {
      try {
        const request: CreateActivityReportInput = {
            originId: certification.originId,
            projectId: certification.projectId,
            supervisorId: certification.userId,
            documentDate: certification.date,
            isCertification: true,
            activities: [],
        };
  
        const certificationItems = await this.certificationItemRepository.getAllByActivityReportId(certification.originId);
        request.activities = certificationItems
        .filter(item => item.quantity && item.quantity > 0)
        .map(item => ({
            roadId: certification.roadId,
            activityId: item.activityId,
            quantity: item.quantity,
            consumptions: [],
        }));
  
        await firstValueFrom(this.activityReportService.createByInput({
          ...request,
          originApplication: 'RoadsProjectManagement',
        } as any));
  
        await this.certificationRepository.setPendingSendByOriginId(certification.originId, false);
      } catch (error) {
        this.toaster.warn(
            `${this.localization.instant('::Synchronization:PendingCertificationError')}`
        );
        const log: SyncLog = {
            originId: certification.originId,
            errorMessage: error.error.error.message,
            action: ActionEnum.Create,
            activityReport: ActivityReportEnum.Certification,
            createdAt: new Date().toISOString(),
        };
        this.syncLogRepository.create(log);
      }
    }

    private async rejectPendingCertifications(certification: Certification) {
      try {
        await firstValueFrom(this.activityReportService.rejectBySourceId(certification.originId));
        await this.certificationRepository.setPendingSendByOriginId(certification.originId, false);
      } catch (error) {
        this.toaster.warn(
            `${this.localization.instant('::Synchronization:PendingCertificationError')}`
        );
        const log: SyncLog = {
            originId: certification.originId,
            errorMessage: error.error.error.message,
            action: ActionEnum.Reject,
            activityReport: ActivityReportEnum.Certification,
            createdAt: new Date().toISOString(),
        };
        this.syncLogRepository.create(log);
      }
    }

    private async resolveCurrentUser(user?: User): Promise<User | undefined> {
        if (user) {
            return user;
        }

        const email = this.configStateService.getDeep('currentUser.email');
        if (!email) {
            return undefined;
        }

        const cachedUser = await this.userRepository.getByEmail(email);
        if (cachedUser) {
            return cachedUser;
        }

        try {
            const result = await firstValueFrom(this.userService.getByEmail(email));
            if (!result) {
                return undefined;
            }

            const resolvedUser = new User(result.id, email, result.vendorId, result.name);
            await this.userRepository.clear();
            await this.userRepository.upsert(resolvedUser);
            return resolvedUser;
        } catch {
            return undefined;
        }
    }
}
